import { useState, useEffect } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

interface AboutContent {
  hero_title: string;
  hero_subtitle: string;
  mission_title: string;
  mission_text_1: string;
  mission_text_2: string;
  mission_text_3: string;
  mission_image_url: string;
  values_title: string;
  values_subtitle: string;
  value_1_title: string;
  value_1_icon: string;
  value_1_description: string;
  value_2_title: string;
  value_2_icon: string;
  value_2_description: string;
  value_3_title: string;
  value_3_icon: string;
  value_3_description: string;
  story_title: string;
  story_text_1: string;
  story_text_2: string;
  story_text_3: string;
  story_image_url: string;
  team_title: string;
  team_subtitle: string;
  team_member_1_name: string;
  team_member_1_role: string;
  team_member_1_specialty: string;
  team_member_1_image: string;
  team_member_2_name: string;
  team_member_2_role: string;
  team_member_2_specialty: string;
  team_member_2_image: string;
  team_member_3_name: string;
  team_member_3_role: string;
  team_member_3_specialty: string;
  team_member_3_image: string;
  cta_title: string;
  cta_subtitle: string;
}

export default function About() {
  const [content, setContent] = useState<AboutContent | null>(null);

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const res = await fetch('https://therevulist.com/wp-json/wp/v2/pages?slug=about');
        const data = await res.json();
        if (Array.isArray(data) && data.length > 0 && data[0].acf) {
          setContent(data[0].acf);
        } else {
          console.error('No ACF content found for about page');
        }
      } catch (error) {
        console.error('Failed to fetch about page content', error);
      }
    };
    fetchContent();
  }, []);

  if (!content) return <div className="min-h-screen flex items-center justify-center">Loading...</div>;

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero */}
      <section className="relative bg-gradient-to-br from-teal-600 to-teal-700 text-white py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl font-bold mb-6">{content.hero_title}</h1>
          <p className="text-xl text-teal-50 max-w-3xl mx-auto">{content.hero_subtitle}</p>
        </div>
      </section>

      {/* Mission */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-2 gap-12 items-center">
          <div>
            <h2 className="text-4xl font-bold text-gray-900 mb-6">{content.mission_title}</h2>
            <p className="text-lg text-gray-600 mb-4">{content.mission_text_1}</p>
            <p className="text-lg text-gray-600 mb-4">{content.mission_text_2}</p>
            <p className="text-lg text-gray-600">{content.mission_text_3}</p>
          </div>
          <div className="relative h-96 rounded-2xl overflow-hidden shadow-2xl">
            <img src={content.mission_image_url} alt="Mission" className="w-full h-full object-cover object-top" />
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">{content.values_title}</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">{content.values_subtitle}</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[1,2,3].map(i => (
              <div key={i} className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300">
                <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mb-6">
                  <i className={`${content[`value_${i}_icon` as keyof AboutContent]} text-3xl text-teal-600`}></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">{content[`value_${i}_title` as keyof AboutContent]}</h3>
                <p className="text-gray-600">{content[`value_${i}_description` as keyof AboutContent]}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Story */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-2 gap-12 items-center">
          <div className="relative h-96 rounded-2xl overflow-hidden shadow-2xl order-2 md:order-1">
            <img src={content.story_image_url} alt="Story" className="w-full h-full object-cover object-top" />
          </div>
          <div className="order-1 md:order-2">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">{content.story_title}</h2>
            <p className="text-lg text-gray-600 mb-4">{content.story_text_1}</p>
            <p className="text-lg text-gray-600 mb-4">{content.story_text_2}</p>
            <p className="text-lg text-gray-600">{content.story_text_3}</p>
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">{content.team_title}</h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">{content.team_subtitle}</p>

          <div className="grid md:grid-cols-3 gap-8 mt-12">
            {[1,2,3].map(i => (
              <div key={i} className="text-center group">
                <div className="relative h-64 rounded-xl overflow-hidden mb-4 shadow-lg group-hover:shadow-xl transition-shadow duration-300">
                  <img src={content[`team_member_${i}_image` as keyof AboutContent]} alt={content[`team_member_${i}_name` as keyof AboutContent]} className="w-full h-full object-cover object-top" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-1">{content[`team_member_${i}_name` as keyof AboutContent]}</h3>
                <p className="text-teal-600 font-semibold mb-1">{content[`team_member_${i}_role` as keyof AboutContent]}</p>
                <p className="text-gray-600 text-sm">{content[`team_member_${i}_specialty` as keyof AboutContent]}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-br from-teal-600 to-teal-700 text-white text-center">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-6">{content.cta_title}</h2>
          <p className="text-xl text-teal-50 mb-8">{content.cta_subtitle}</p>
          <div className="flex flex-wrap gap-4 justify-center">
            <button className="px-8 py-4 bg-white text-teal-600 rounded-lg font-semibold hover:bg-teal-50 transition-colors duration-300">Find a Provider</button>
            <button className="px-8 py-4 bg-teal-500 text-white rounded-lg font-semibold hover:bg-teal-400 transition-colors duration-300">List Your Practice</button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
